import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  DollarSign, Download, FileText, Loader2, Wallet, TrendingUp, 
  TrendingDown, Calendar, Building2, CreditCard, Printer
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const MySalary = () => {
  const [salary, setSalary] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [payslipDetail, setPayslipDetail] = useState(null);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [loadingPayslip, setLoadingPayslip] = useState(false);
  const [canPrintPayslip, setCanPrintPayslip] = useState(true);

  const { getAuthHeaders, user } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();

      // Fetch salary
      const salaryRes = await fetch(`${API_URL}/api/payroll/my-salary`, { headers });
      if (salaryRes.ok) {
        setSalary(await salaryRes.json());
      }

      // Fetch payslips
      const payslipsRes = await fetch(`${API_URL}/api/payroll/my-payslips`, { headers });
      if (payslipsRes.ok) {
        setPayslips(await payslipsRes.json());
      }

      // Fetch payslip printing permission
      const printSettingRes = await fetch(`${API_URL}/api/offices/settings/payslip-printing`, { headers });
      if (printSettingRes.ok) {
        const printData = await printSettingRes.json();
        setCanPrintPayslip(printData.allow_employee_payslip_printing);
      }
    } catch (error) {
      console.error('Error fetching salary data:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewPayslip = async (payslip) => {
    setSelectedPayslip(payslip);
    setLoadingPayslip(true);
    setShowPayslipModal(true);

    try {
      const headers = getAuthHeaders();
      // Use the employee_id from the payslip if available, otherwise fall back to user.id
      const employeeId = payslip.employee_id || user.id;
      const res = await fetch(
        `${API_URL}/api/payroll/payslip/${payslip.payroll_id}/${employeeId}`,
        { headers }
      );
      if (res.ok) {
        setPayslipDetail(await res.json());
      }
    } catch (error) {
      console.error('Error fetching payslip detail:', error);
    } finally {
      setLoadingPayslip(false);
    }
  };

  const printPayslip = () => {
    window.print();
  };

  const formatCurrency = (amount, currency) => {
    const currencyCode = currency || salary?.currency || 'USD';
    try {
      return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    } catch {
      return `${currencyCode} ${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    }
  };

  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1] || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-salary-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Salary</h1>
        <p className="text-gray-600 dark:text-gray-400">View your salary details and payslips</p>
      </div>

      <Tabs defaultValue="salary">
        <TabsList>
          <TabsTrigger value="salary">Current Salary</TabsTrigger>
          <TabsTrigger value="payslips">Payslips ({payslips.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="salary">
          {salary ? (
            <div className="grid gap-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600 mb-1">Basic Salary</p>
                        <p className="text-sm sm:text-base md:text-lg font-bold leading-tight break-all">{formatCurrency(salary.basic_salary)}</p>
                      </div>
                      <div className="p-2 sm:p-3 bg-blue-100 rounded-lg flex-shrink-0">
                        <Wallet className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600 mb-1">Allowances</p>
                        <p className="text-sm sm:text-base md:text-lg font-bold text-green-600 leading-tight break-all">+{formatCurrency(salary.total_allowances)}</p>
                      </div>
                      <div className="p-2 sm:p-3 bg-green-100 rounded-lg flex-shrink-0">
                        <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600 mb-1">Deductions</p>
                        <p className="text-sm sm:text-base md:text-lg font-bold text-red-600 leading-tight break-all">-{formatCurrency(salary.total_deductions)}</p>
                      </div>
                      <div className="p-2 sm:p-3 bg-red-100 rounded-lg flex-shrink-0">
                        <TrendingDown className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-950 dark:to-emerald-900">
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600 mb-1">Net Salary</p>
                        <p className="text-sm sm:text-base md:text-lg font-bold text-green-700 leading-tight break-all">{formatCurrency(salary.net_salary)}</p>
                      </div>
                      <div className="p-2 sm:p-3 bg-green-200 rounded-lg flex-shrink-0">
                        <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-green-700" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Salary Breakdown */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Allowances */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                      Allowances
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {salary.allowances?.length > 0 ? (
                      <div className="space-y-3">
                        {salary.allowances.map((allowance, index) => (
                          <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                            <div>
                              <p className="font-medium capitalize">{allowance.name || allowance.type}</p>
                              <p className="text-xs text-gray-500 capitalize">{allowance.type}</p>
                            </div>
                            <span className="font-semibold text-green-600">+{formatCurrency(allowance.amount)}</span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between pt-3 border-t font-bold">
                          <span>Total Allowances</span>
                          <span className="text-green-600">{formatCurrency(salary.total_allowances)}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No allowances</p>
                    )}
                  </CardContent>
                </Card>

                {/* Deductions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingDown className="h-5 w-5 text-red-600" />
                      Deductions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {salary.deductions?.length > 0 ? (
                      <div className="space-y-3">
                        {salary.deductions.map((deduction, index) => (
                          <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                            <div>
                              <p className="font-medium capitalize">{deduction.name || deduction.type}</p>
                              <p className="text-xs text-gray-500 capitalize">{deduction.type}</p>
                            </div>
                            <span className="font-semibold text-red-600">-{formatCurrency(deduction.amount)}</span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between pt-3 border-t font-bold">
                          <span>Total Deductions</span>
                          <span className="text-red-600">{formatCurrency(salary.total_deductions)}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No deductions</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Bank Details */}
              {(salary.bank_name || salary.account_number) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Bank Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Bank Name</p>
                        <p className="font-medium">{salary.bank_name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Account Number</p>
                        <p className="font-medium">{salary.account_number || '-'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Wallet className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">No salary record found</p>
                <p className="text-sm text-gray-400 mt-2">Contact your HR administrator for assistance</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="payslips">
          <Card>
            <CardHeader>
              <CardTitle>Payslip History</CardTitle>
            </CardHeader>
            <CardContent>
              {payslips.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium">Period</th>
                        <th className="text-right py-3 px-4 font-medium">Gross</th>
                        <th className="text-right py-3 px-4 font-medium">Deductions</th>
                        <th className="text-right py-3 px-4 font-medium">Net Pay</th>
                        <th className="text-center py-3 px-4 font-medium">Pay Date</th>
                        <th className="text-center py-3 px-4 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payslips.map((payslip, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="font-medium">
                                {getMonthName(payslip.period_month)} {payslip.period_year}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right">{formatCurrency(payslip.gross_salary, payslip.currency)}</td>
                          <td className="py-3 px-4 text-right text-red-600">-{formatCurrency(payslip.total_deductions, payslip.currency)}</td>
                          <td className="py-3 px-4 text-right font-bold text-green-600">{formatCurrency(payslip.net_salary, payslip.currency)}</td>
                          <td className="py-3 px-4 text-center">
                            {payslip.pay_date ? new Date(payslip.pay_date).toLocaleDateString() : '-'}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Button variant="outline" size="sm" onClick={() => viewPayslip(payslip)}>
                              <FileText className="h-4 w-4 mr-1" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No payslips available yet</p>
                  <p className="text-sm text-gray-400 mt-2">Payslips will appear here after payroll is processed</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payslip Detail Modal */}
      <Dialog open={showPayslipModal} onOpenChange={setShowPayslipModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto print:max-w-full print:max-h-full">
          <DialogHeader className="print:hidden">
            <DialogTitle>
              Payslip - {selectedPayslip && `${getMonthName(selectedPayslip.period_month)} ${selectedPayslip.period_year}`}
            </DialogTitle>
          </DialogHeader>
          
          {loadingPayslip ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : payslipDetail ? (
            <div className="space-y-6" id="payslip-content">
              {/* Header */}
              <div className="border-b pb-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl font-bold">{payslipDetail.office?.company_name || payslipDetail.office?.name}</h2>
                    <p className="text-sm text-gray-500">{payslipDetail.office?.location}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">PAYSLIP</p>
                    <p className="text-sm text-gray-500">
                      {getMonthName(payslipDetail.period_month)} {payslipDetail.period_year}
                    </p>
                  </div>
                </div>
              </div>

              {/* Employee Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">Employee Name</p>
                  <p className="font-medium">{payslipDetail.employee?.name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pay Date</p>
                  <p className="font-medium">
                    {payslipDetail.pay_date ? new Date(payslipDetail.pay_date).toLocaleDateString() : '-'}
                  </p>
                </div>
              </div>

              {/* Earnings & Deductions */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Earnings */}
                <div>
                  <h3 className="font-semibold mb-3 text-green-700">Earnings</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between py-1">
                      <span>Basic Salary</span>
                      <span>{formatCurrency(payslipDetail.earnings?.basic_salary, payslipDetail.currency)}</span>
                    </div>
                    {payslipDetail.earnings?.allowances?.map((a, i) => (
                      <div key={i} className="flex justify-between py-1 text-sm">
                        <span className="capitalize">{a.name || a.type}</span>
                        <span>{formatCurrency(a.amount, payslipDetail.currency)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between py-2 border-t font-semibold">
                      <span>Gross Earnings</span>
                      <span>{formatCurrency(payslipDetail.earnings?.gross_salary, payslipDetail.currency)}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions */}
                <div>
                  <h3 className="font-semibold mb-3 text-red-700">Deductions</h3>
                  <div className="space-y-2">
                    {payslipDetail.deductions?.items?.length > 0 ? (
                      payslipDetail.deductions.items.map((d, i) => (
                        <div key={i} className="flex justify-between py-1 text-sm">
                          <span className="capitalize">{d.name || d.type}</span>
                          <span>{formatCurrency(d.amount, payslipDetail.currency)}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No deductions</p>
                    )}
                    <div className="flex justify-between py-2 border-t font-semibold">
                      <span>Total Deductions</span>
                      <span>{formatCurrency(payslipDetail.deductions?.total_deductions, payslipDetail.currency)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Pay */}
              <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Net Pay</span>
                  <span className="text-2xl font-bold text-green-700">
                    {formatCurrency(payslipDetail.net_salary, payslipDetail.currency)}
                  </span>
                </div>
              </div>

              {/* Bank Details */}
              {(payslipDetail.bank_details?.bank_name || payslipDetail.bank_details?.account_number) && (
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h3 className="font-semibold mb-2">Payment Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Bank</p>
                      <p>{payslipDetail.bank_details?.bank_name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Account</p>
                      <p>{payslipDetail.bank_details?.account_number || '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 print:hidden">
                {canPrintPayslip && (
                  <Button variant="outline" onClick={printPayslip} data-testid="print-payslip-btn">
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Failed to load payslip details</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MySalary;
